import { motion } from "framer-motion";
import { useEffect } from "react";
import { useScene } from "./scene/SceneController";
import BaseIcon from "../../../components/svg_icons/BaseIcon";

const brandName = process.env.REACT_APP_BRAND_NAME;

const features = [
    {
        title: "Intelligent Document Flow",
        description: "Invoices, receipts and client documents automatically move into the right workflow.",
    },
    {
        title: "Real-Time Firm Visibility",
        description: "Partners see progress, bottlenecks and important updates without chasing information.",
    },
    {
        title: "Assisted Decision Making",
        description: `${brandName} prepares insights while your accountants make the final decisions.`,
    },
    {
        title: "Connected Client Operations",
        description: "Every client interaction, file and process stays organized in one system.",
    },
];

const WhatWeOffer = [
    { title: "Documents", text: "Connected automatically1", icon: "fileText", },
    { title: "Clients",   text: "Connected automatically2", icon: "users", },
    { title: "Tasks",     text: "Connected automatically3", icon: "listTodo", },
    { title: "Insights",  text: "Connected automatically4", icon: "brainCircuit", },
]

const SolutionSection = () => {
    const { updateScene } = useScene();

    useEffect(() => {
        updateScene({
            section: "solution",
            chaos: 0,
            organization: 1,
            coreIntensity: 2.5,
            cameraDistance: 7,
        });
    }, [updateScene]);

    return (
        <section
            id="solution"
            className="tw-relative tw-min-h-screen tw-pt-28 tw-pb-16 tw-px-6 tw-overflow-hidden"
        >
            {/* Background */}

            <div
                className="
                    tw-absolute
                    tw-inset-0
                    tw-bg-gradient-to-b
                    tw-from-[#050816]
                    tw-via-blue-950/20
                    tw-to-[#f8fafc]
                "
            />

            <motion.div
                animate={{ opacity:[0.2,0.5,0.2] }}
                transition={{ duration:8, repeat:Infinity }}
                className="
                    tw-absolute
                    tw-top-0
                    tw-left-1/2
                    -tw-translate-x-1/2
                    tw-w-[600px]
                    tw-h-[200px]
                    tw-bg-cyan-500/20
                    tw-blur-[120px]
                "
            />

            <div
                className="
                    tw-relative
                    tw-z-10
                    tw-max-w-6xl
                    tw-mx-auto
                "
            >
                {/* Heading */}

                <motion.div
                    initial={{
                        opacity: 0,
                        y: 40,
                    }}
                    whileInView={{
                        opacity: 1,
                        y: 0,
                    }}
                    viewport={{
                        once: true,
                    }}
                    transition={{
                        duration: 0.8,
                    }}
                    className="
                        tw-text-center
                        tw-max-w-4xl
                        tw-mx-auto
                    "
                >
                    <p
                        className="
                            tw-text-cyan-400
                            tw-uppercase
                            tw-tracking-[0.3em]
                            tw-text-sm
                            tw-mb-5
                        "
                    >
                        Meet {brandName}
                    </p>

                    <h2
                        className="
                            tw-text-4xl
                            md:tw-text-6xl
                            tw-font-bold
                            tw-text-white
                        "
                    >
                        Your firm's intelligence layer.
                    </h2>

                    <p
                        className="
                            tw-mt-6
                            tw-text-lg
                            tw-text-gray-300
                            tw-leading-relaxed
                        "
                    >
                        A connected operating system that organizes your workflows,
                        automates repetitive tasks and helps every accountant focus on
                        higher value work.
                    </p>
                </motion.div>

                {/* Product visualization */}

                <motion.div
                    initial={{
                        opacity: 0,
                        scale: 0.9,
                    }}
                    whileInView={{
                        opacity: 1,
                        scale: 1,
                    }}
                    viewport={{
                        once: true,
                    }}
                    transition={{
                        duration: 1,
                    }}
                    className="
                        tw-mt-20
                        tw-rounded-3xl
                        tw-p-8
                        md:tw-p-12
                        tw-bg-white/5
                        tw-backdrop-blur-2xl
                        tw-border
                        tw-border-white/10
                        tw-shadow-2xl
                    "
                >
                    {/* Fake dashboard */}

                    <div
                        className="
                            tw-flex
                            tw-flex-col
                            tw-gap-6
                        "
                    >
                        <div
                            className="
                                tw-flex
                                tw-items-center
                                tw-justify-between
                                tw-p-5
                                tw-rounded-2xl
                                tw-bg-black/30
                                tw-border
                                tw-border-white/10
                            "
                        >
                            <div>
                                <p className="tw-text-gray-400 tw-text-sm">
                                    {brandName} Workspace
                                </p>

                                <h3
                                    className="
                                        tw-text-white
                                        tw-text-2xl
                                        tw-font-bold
                                        tw-mt-1
                                    "
                                >
                                    Modern CA Operations
                                </h3>
                            </div>

                            <div
                                className="
                                    tw-w-12
                                    tw-h-12
                                    tw-rounded-full
                                    tw-bg-gradient-to-r
                                    tw-from-blue-500
                                    tw-to-cyan-400
                                    tw-shadow-lg
                                "
                            >
                                {/* <BaseIcon name={"aILogo"} /> */}
                            </div>
                        </div>

                        <div
                            className="
                                tw-grid
                                md:tw-grid-cols-4
                                tw-gap-5
                            "
                        >
                            { WhatWeOffer.map(
                                (item, index) => (
                                    <motion.div
                                        key={item.title}
                                        initial={{
                                            opacity: 0,
                                            y: 20,
                                        }}
                                        whileInView={{
                                            opacity: 1,
                                            y: 0,
                                        }}
                                        viewport={{
                                            once: true,
                                        }}
                                        transition={{
                                            delay: index * 0.15,
                                        }}
                                        className="
                                            tw-rounded-2xl
                                            tw-p-6
                                            tw-bg-white
                                            tw-text-gray-900
                                            tw-shadow-xl
                                        "
                                    >
                                        <div
                                            className="
                                                tw-w-10
                                                tw-h-10
                                                tw-rounded-xl
                                                tw-bg-blue-100
                                                tw-flex
                                                tw-items-center
                                                tw-justify-center
                                                tw-mb-5
                                            "
                                        >
                                            <BaseIcon
                                                name = { item.icon }
                                                className="tw-text-blue-600"
                                            />
                                        </div>

                                        <h4
                                            className="
                                                tw-font-semibold
                                            "
                                        >
                                            {item.title}
                                        </h4>

                                        <p className="tw-text-sm tw-text-gray-500 tw-mt-2">
                                            {item.text}
                                        </p>
                                    </motion.div>
                                )
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* Feature grid */}
                <div className="tw-mt-20 tw-grid md:tw-grid-cols-2 tw-gap-8">
                    {features.map((feature, index) => (
                        <motion.div
                            key={feature.title}
                            initial={{
                                opacity: 0,
                                y: 40,
                            }}
                            whileInView={{
                                opacity: 1,
                                y: 0,
                            }}
                            whileHover={{
                                y: -8,
                            }}
                            viewport={{
                                once: true,
                            }}
                            transition={{
                                delay: index * 0.15,
                                duration: 0.5,
                            }}
                            className="
                                tw-group
                                tw-rounded-3xl
                                tw-p-8
                                tw-bg-black/60
                                tw-backdrop-blur-xl
                                tw-border
                                tw-border-white/60
                                tw-shadow-lg
                                hover:tw-shadow-2xl
                                hover:tw-shadow-cyan-500/20
                                tw-transition-all
                                tw-duration-300
                            "
                        >
                            {/* Accent line */}
                            <div
                                className="
                                    tw-w-12
                                    tw-h-1
                                    tw-rounded-full
                                    tw-bg-gradient-to-r
                                    tw-from-blue-500
                                    tw-to-cyan-400
                                    tw-mb-6
                                    group-hover:tw-w-20
                                    tw-transition-all
                                    tw-duration-300
                                "
                            />

                            <h3 className="tw-text-xl tw-font-semibold tw-text-white-900 tw-mb-3">
                                {feature.title}
                            </h3>

                            <p className="tw-text-gray-600leading-relaxed">
                                {feature.description}
                            </p>

                            {/* Bottom indicator */}
                            <div
                                className="
                                    tw-mt-6
                                    tw-flex
                                    tw-items-center
                                    tw-gap-2
                                    tw-text-sm
                                    tw-text-white
                                    tw-transition-all
                                    tw-duration-300
                                "
                            >
                                {/* <span
                                    className="
                                        tw-w-2
                                        tw-h-2
                                        tw-rounded-full
                                        tw-bg-white
                                        group-hover:tw-bg-gradient-to-r
                                        group-hover:tw-from-blue-500
                                        group-hover:tw-via-cyan-400
                                        group-hover:tw-to-purple-500
                                        group-hover:tw-animate-pulse
                                    "
                                /> */}

                                <span
                                    className="
                                        tw-text-white
                                        tw-transition-all
                                        tw-duration-300

                                        group-hover:tw-text-transparent
                                        group-hover:tw-bg-gradient-to-r
                                        group-hover:tw-from-blue-500
                                        group-hover:tw-via-cyan-400
                                        group-hover:tw-to-purple-500
                                        group-hover:tw-bg-[length:200%_100%]
                                        group-hover:tw-bg-clip-text
                                        group-hover:tw-animate-gradient
                                    "
                                >
                                    Connected intelligence
                                </span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default SolutionSection;